import { z } from 'zod';

/**
 * Teacher reports: templates, window settings, point rules, and the shapes
 * the API returns. See docs/teacher-reports-implementation-plan.md.
 *
 * Money is integer paise. Times are `HH:MM` in academy time (Asia/Kolkata).
 */

export const reportKindSchema = z.enum(['daily', 'weekly', 'monthly']);
export type ReportKind = z.infer<typeof reportKindSchema>;
export const reportModeSchema = z.enum(['online', 'offline']);
export type ReportMode = z.infer<typeof reportModeSchema>;

const timeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/u, 'Use HH:MM (24-hour)');
const itemKeySchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]{1,47}$/u, 'Keys are lowercase letters, digits and underscores');

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

/**
 * How an item earns its points:
 *   check   — the teacher ticks it (self-reported)
 *   auto    — the portal checks it (see `verify`); the teacher can't tick it
 *   share   — the teacher shares a prepared WhatsApp message from the portal
 *   upload  — the teacher attaches a file
 *   counter — the teacher enters a number; points when it reaches `target`
 */
export const reportItemTypeSchema = z.enum(['check', 'auto', 'share', 'upload', 'counter']);
export type ReportItemType = z.infer<typeof reportItemTypeSchema>;

/** Portal facts an `auto` item can be verified against. */
export const reportVerifySchema = z.enum(['attendance_marked', 'exam_marks_entered']);
export type ReportVerify = z.infer<typeof reportVerifySchema>;

export const reportItemSchema = z
  .object({
    key: itemKeySchema,
    label: z.string().trim().min(3).max(160),
    labelMl: z.string().trim().max(200).optional(),
    type: reportItemTypeSchema,
    points: z.number().int().min(0).max(20),
    target: z.number().int().min(1).max(100).optional(),
    verify: reportVerifySchema.optional(),
    hint: z.string().trim().max(200).optional(),
    hintMl: z.string().trim().max(300).optional(),
    /** Hide the item when it can't apply, e.g. no one was absent. */
    showWhen: z.enum(['someone_absent', 'fees_pending']).optional(),
  })
  .strict()
  .superRefine((item, ctx) => {
    if (item.type === 'counter' && item.target === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['target'], message: 'A counter needs a target' });
    }
    if (item.type === 'auto' && !item.verify) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['verify'], message: 'Choose what the portal checks' });
    }
    if (item.type !== 'auto' && item.verify) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['verify'], message: 'Only portal-checked items can verify' });
    }
  });
export type ReportItem = z.infer<typeof reportItemSchema>;

export const reportFieldSchema = z
  .object({
    key: itemKeySchema,
    label: z.string().trim().min(3).max(160),
    labelMl: z.string().trim().max(200).optional(),
    type: z.enum(['text', 'students']),
    placeholder: z.string().trim().max(200).optional(),
    placeholderMl: z.string().trim().max(300).optional(),
    /** Counts toward the template's reflection points (monthly). */
    reflect: z.boolean().optional(),
  })
  .strict();
export type ReportField = z.infer<typeof reportFieldSchema>;

export const reportGroupsConfigSchema = z
  .object({
    /** Show each student's % (weekly / monthly). */
    withPercent: z.boolean(),
    /** Ask for each group's main problem and toughest subject (monthly). */
    askProblemAndSubject: z.boolean(),
    topMin: z.number().int().min(1).max(100),
    mediumMin: z.number().int().min(0).max(99),
  })
  .strict()
  .refine((g) => g.mediumMin < g.topMin, { message: 'Medium must start below Top', path: ['mediumMin'] });
export type ReportGroupsConfig = z.infer<typeof reportGroupsConfigSchema>;

const templateBodyFields = {
  title: z.string().trim().min(3).max(80),
  basePoints: z.number().int().min(0).max(100),
  groupsPoints: z.number().int().min(0).max(50),
  reflectPoints: z.number().int().min(0).max(50),
  items: z.array(reportItemSchema).min(1).max(30),
  fields: z.array(reportFieldSchema).max(6),
  groups: reportGroupsConfigSchema,
  /** Offer an optional voice note at the end of the report (default on). */
  voiceNote: z.boolean().optional(),
};

const uniqueKeys = (
  value: { items: ReadonlyArray<{ key: string }>; fields: ReadonlyArray<{ key: string }> },
  ctx: z.RefinementCtx,
): void => {
  const seen = new Set<string>();
  [...value.items, ...value.fields].forEach((entry, i) => {
    if (seen.has(entry.key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [i < value.items.length ? 'items' : 'fields'],
        message: `Duplicate key "${entry.key}"`,
      });
    }
    seen.add(entry.key);
  });
};

/** Body for saving a draft template version. */
export const reportTemplateBodySchema = z.object(templateBodyFields).strict().superRefine(uniqueKeys);
export type ReportTemplateBody = z.infer<typeof reportTemplateBodySchema>;

/** Start a new draft: copy the active (or given) version of kind × mode. */
export const reportTemplateDraftCreateSchema = z
  .object({ kind: reportKindSchema, mode: reportModeSchema, fromTemplateId: z.string().uuid().optional() })
  .strict();

export interface ReportTemplateDto extends ReportTemplateBody {
  id: string;
  kind: ReportKind;
  mode: ReportMode;
  version: number;
  status: 'draft' | 'active' | 'retired';
  publishedAt: string | null;
  updatedAt: string;
  maxPoints: number;
  voiceNote: boolean;
}

/** Most points a report on this template can earn (before bonuses). */
export const templateMaxPoints = (t: Pick<ReportTemplateBody, 'basePoints' | 'groupsPoints' | 'reflectPoints' | 'items'>): number =>
  t.basePoints + t.groupsPoints + t.reflectPoints + t.items.reduce((sum, i) => sum + i.points, 0);

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export const reportWindowSettingsSchema = z
  .object({
    daily: z
      .object({
        /**
         * `class_end`: opens when the class ends (or earlier, once attendance
         * is finalised, if `openEarlyOnAttendance`). `attendance_finalised`:
         * opens only once attendance is finalised.
         */
        opens: z.enum(['class_end', 'attendance_finalised']),
        openEarlyOnAttendance: z.boolean(),
        dueTime: timeSchema,
        minHoursAfterClass: z.number().min(0).max(12),
        graceUntilNextDay: timeSchema,
      })
      .strict(),
    weekly: z
      .object({
        weekStarts: z.enum(['mon', 'sat', 'sun']),
        dueDay: z.enum(['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']),
        dueTime: timeSchema,
      })
      .strict(),
    monthly: z
      .object({
        dueDayOfNextMonth: z.number().int().min(1).max(28),
        dueTime: timeSchema,
      })
      .strict(),
    /** Batches whose timetable is empty or unreadable get Mon–Fri classes ending here. */
    fallbackClassEnd: timeSchema,
    /**
     * No longer used: the reminder time is set on System → Scheduled jobs
     * ("Teacher report reminder"). Kept optional so settings saved before
     * still load.
     */
    reminders: z.object({ dailyAt: timeSchema }).strict().optional(),
  })
  .strict();
export type ReportWindowSettings = z.infer<typeof reportWindowSettingsSchema>;

export const reportPointRulesSchema = z
  .object({
    ratePaise: z.number().int().min(0).max(100_000),
    capPaise: z.number().int().min(0).max(100_000_000),
    streakLength: z.number().int().min(2).max(60),
    streakBonus: z.number().int().min(0).max(500),
    perfectMonthBonus: z.number().int().min(0).max(1000),
    maxQualityBonus: z.number().int().min(0).max(50),
  })
  .strict();
export type ReportPointRules = z.infer<typeof reportPointRulesSchema>;

export const reportSettingsSchema = z
  .object({
    windows: reportWindowSettingsSchema,
    points: reportPointRulesSchema,
    /** Empty = every teacher (once the module flag is on). */
    pilotTeacherIds: z.array(z.string().uuid()).max(200),
    /** Days a report's voice note audio is kept before it's deleted. */
    voiceNoteRetentionDays: z.number().int().min(30).max(1825),
  })
  .strict();
export type ReportSettings = z.infer<typeof reportSettingsSchema>;

export const reportSettingsUpdateSchema = z
  .object({
    windows: reportWindowSettingsSchema.optional(),
    points: reportPointRulesSchema.optional(),
    pilotTeacherIds: z.array(z.string().uuid()).max(200).optional(),
    voiceNoteRetentionDays: z.number().int().min(30).max(1825).optional(),
  })
  .strict();
export type ReportSettingsUpdate = z.infer<typeof reportSettingsUpdateSchema>;

export const DEFAULT_REPORT_SETTINGS: ReportSettings = {
  windows: {
    daily: {
      opens: 'class_end',
      openEarlyOnAttendance: true,
      dueTime: '23:00',
      minHoursAfterClass: 2,
      graceUntilNextDay: '09:00',
    },
    weekly: { weekStarts: 'mon', dueDay: 'sun', dueTime: '20:00' },
    monthly: { dueDayOfNextMonth: 7, dueTime: '23:59' },
    fallbackClassEnd: '18:00',
  },
  points: {
    ratePaise: 100,
    capPaise: 150_000,
    streakLength: 6,
    streakBonus: 10,
    perfectMonthBonus: 50,
    maxQualityBonus: 5,
  },
  pilotTeacherIds: [],
  voiceNoteRetentionDays: 365,
};

export const reportSettingsPreviewSchema = z
  .object({
    windows: reportWindowSettingsSchema.optional(),
    batchIds: z.array(z.string().uuid()).max(10).optional(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u).optional(),
  })
  .strict();

// ---------------------------------------------------------------------------
// Expected reports (what the teacher owes, and when)
// ---------------------------------------------------------------------------

export type ReportWindowStatus = 'locked' | 'open' | 'late' | 'closed';

export interface ExpectedReportDto {
  kind: ReportKind;
  batchId: string;
  batchName: string;
  teacherId: string;
  mode: ReportMode;
  periodKey: string;
  periodStart: string;
  periodEnd: string;
  earnedMonth: string;
  opensAt: string;
  dueAt: string;
  graceUntil: string;
  status: ReportWindowStatus;
  /** Why it opens when it does, in plain words ("when today's class ends"). */
  opensBecause: string;
  classCount: number;
  maxPoints: number;
  templateId: string | null;
  /** Filled once a report exists (Phase 3). */
  report: { id: string; status: string; pointsTotal: number | null; submittedAt: string | null } | null;
}

export interface MyDueReportsResponse {
  now: string;
  /** Open or late (grace) reports not yet submitted, soonest due first. */
  open: ExpectedReportDto[];
  /** The next locked report per batch and kind. */
  upcoming: ExpectedReportDto[];
  /** Submitted in the recent window (Phase 3). */
  recent: ExpectedReportDto[];
}
